"use client";

import { a, useSpring } from "@react-spring/three";
import { Decal, useTexture } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useViewportSize } from "@ui/components/helpers/useViewportSize";
import clsx from "clsx";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMove } from "react-aria";
import * as THREE from "three";

export type AlbumCover = {
  id: string;
  imageUrl: string;
  audioUrl?: string;
  description?: string;
};

const RESPONSIVENESS = 1;

// Calculate responsive radius based on viewport width
function getResponsiveRadius(viewportWidth: number): number {
  const minWidth = 320; // Mobile breakpoint
  const maxWidth = 1200; // Desktop breakpoint
  const minRadius = 4; // Minimum radius for mobile (larger cards)
  const maxRadius = 4; // Maximum radius for desktop

  if (viewportWidth <= minWidth) return minRadius;
  if (viewportWidth >= maxWidth) return maxRadius;

  // Linear interpolation between min and max radius
  const ratio = (viewportWidth - minWidth) / (maxWidth - minWidth);
  return minRadius + (maxRadius - minRadius) * ratio;
}

export function AlbumCoverVisual({
  activeCoverIndex,
  covers,
  onActiveCoverIndexChange,
  heightMode = "fixed",
}: {
  activeCoverIndex: number;
  covers: AlbumCover[];
  onActiveCoverIndexChange: (index: number) => void;
  /** Height mode: "fixed" uses fixed responsive heights, "flexible" uses h-full */
  heightMode?: "fixed" | "flexible";
}) {
  const viewport = useViewportSize();
  const radius = getResponsiveRadius(viewport.width);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidthRef = useRef(0);

  // Rotation state - tracks the current Y rotation in radians
  const [rotationY, setRotationY] = useState(() => -(activeCoverIndex / covers.length) * Math.PI * 2);
  const [isDragging, setIsDragging] = useState(false);

  // Ref to track current rotation for use in callbacks (avoids stale closure issues)
  const rotationYRef = useRef(rotationY);

  // Cache container width for delta calculations
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        containerWidthRef.current = containerRef.current.offsetWidth;
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Find the closest decal index based on current rotation
  const findClosestDecalIndex = useCallback(
    (currentRotation: number) => {
      const count = covers.length;
      let closestIndex = 0;
      let smallestAngleDiff = Infinity;

      for (let i = 0; i < count; i++) {
        const decalAngle = -(i / count) * Math.PI * 2;

        // Calculate shortest angular distance
        let diff = currentRotation - decalAngle;
        while (diff > Math.PI) diff -= 2 * Math.PI;
        while (diff < -Math.PI) diff += 2 * Math.PI;

        const absDiff = Math.abs(diff);
        if (absDiff < smallestAngleDiff) {
          smallestAngleDiff = absDiff;
          closestIndex = i;
        }
      }

      return closestIndex;
    },
    [covers.length],
  );

  // Calculate snap rotation for a given index
  const calculateSnapRotation = useCallback(
    (currentRotation: number, targetIndex: number) => {
      const snapRotation = -(targetIndex / covers.length) * Math.PI * 2;

      // Find shortest path to snap rotation
      let diff = snapRotation - currentRotation;
      while (diff > Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;

      return currentRotation + diff;
    },
    [covers.length],
  );

  const { moveProps } = useMove({
    onMoveStart: () => {
      setIsDragging(true);
    },
    onMoveEnd: () => {
      setIsDragging(false);
      // Snap to closest decal - use ref to get current rotation (avoids stale closure)
      const closestIndex = findClosestDecalIndex(rotationYRef.current);
      onActiveCoverIndexChange(closestIndex);
      setRotationY(currentRotation => {
        const snappedRotation = calculateSnapRotation(currentRotation, closestIndex);
        rotationYRef.current = snappedRotation; // Keep ref in sync
        return snappedRotation;
      });
    },
    onMove: e => {
      if (e.pointerType === "keyboard") return;
      const width = containerWidthRef.current || viewport.width;
      const delta = (e.deltaX / width) * RESPONSIVENESS;
      setRotationY(prev => {
        const newRotation = prev + delta;
        rotationYRef.current = newRotation; // Keep ref in sync during drag
        return newRotation;
      });
    },
  });

  // Sync rotation when activeCoverIndex changes externally (e.g., from nav buttons)
  useEffect(() => {
    if (!isDragging) {
      setRotationY(currentRotation => {
        const targetRotation = -(activeCoverIndex / covers.length) * Math.PI * 2;

        // Find shortest path to target rotation
        let diff = targetRotation - currentRotation;
        while (diff > Math.PI) diff -= 2 * Math.PI;
        while (diff < -Math.PI) diff += 2 * Math.PI;

        const newRotation = currentRotation + diff;
        rotationYRef.current = newRotation; // Keep ref in sync
        return newRotation;
      });
    }
  }, [activeCoverIndex, isDragging, covers.length]);

  const heightClass = heightMode === "flexible" ? "h-full" : "h-[400px] lg:h-[500px]";

  return (
    <div
      ref={containerRef}
      {...moveProps}
      className={clsx(
        "mx-auto w-full max-w-[1200px] overflow-hidden",
        heightClass,
        isDragging ? "cursor-grabbing" : "cursor-grab",
      )}
      style={{ touchAction: "none" }}
    >
      <Canvas flat linear>
        <Scene covers={covers} isDragging={isDragging} radius={radius} rotationY={rotationY} />
      </Canvas>
    </div>
  );
}

const Scene = memo(
  ({
    covers,
    radius,
    rotationY,
    isDragging,
  }: {
    covers: AlbumCover[];
    isDragging: boolean;
    radius: number;
    rotationY: number;
  }) => {
    return (
      <>
        <fog args={["#fff", 0, 4 * radius]} attach='fog' />
        <Core covers={covers} isDragging={isDragging} radius={radius} rotationY={rotationY} />
      </>
    );
  },
);

Scene.displayName = "Scene";

function Core({
  covers,
  radius,
  rotationY,
  isDragging,
}: {
  covers: AlbumCover[];
  isDragging: boolean;
  radius: number;
  rotationY: number;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const textures = useTexture(covers.map(cover => cover.imageUrl));

  const [spring, api] = useSpring(() => ({
    rotation: [0, rotationY, 0] as [number, number, number],
    config: { mass: 1, tension: 180, friction: 26, precision: 0.0001 },
  }));

  // Update spring when rotation changes
  useEffect(() => {
    api.start({
      rotation: [0, rotationY, 0],
      // Use immediate updates during drag for responsiveness, animated otherwise
      immediate: isDragging,
      config: isDragging ? { tension: 300, friction: 30 } : { mass: 1, tension: 180, friction: 26 },
    });
  }, [rotationY, isDragging, api]);

  // Generate decal positions around the cylinder
  const decalPositions: { position: [number, number, number]; rotation: [number, number, number] }[] = useMemo(() => {
    const positions: { position: [number, number, number]; rotation: [number, number, number] }[] = [];
    const count = covers.length;

    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.PI / 2; // Add π/2 to face camera
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      const y = 0;

      // Calculate proper rotation using lookAt
      const position = new THREE.Vector3(x, y, z);
      const target = position.clone().multiplyScalar(radius);
      const up = new THREE.Vector3(0, 1, 0);

      const matrix = new THREE.Matrix4().lookAt(target, position, up);
      const rotation = new THREE.Euler().setFromRotationMatrix(matrix);

      positions.push({
        position: [x, y, z],
        rotation: [rotation.x, rotation.y, rotation.z],
      });
    }

    return positions;
  }, [covers.length, radius]);

  return (
    <a.mesh ref={meshRef} rotation={spring.rotation as any}>
      <cylinderGeometry args={[radius, radius, 2, 128]} />
      <meshStandardMaterial opacity={0} transparent />
      {decalPositions.map((decal, index) => (
        <Decal key={index} position={decal.position} rotation={decal.rotation}>
          <meshBasicMaterial
            map={textures[(covers.length - index) % covers.length]}
            side={THREE.DoubleSide}
            transparent
            depthTest={false}
          />
        </Decal>
      ))}
    </a.mesh>
  );
}
